import { useDraggable } from "@dnd-kit/core";
import { AlertTriangle } from "lucide-react";
import type { BoardCard } from "@salesboard/contracts";
import { useT } from "@/i18n";
import { Tooltip } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { useDragClick } from "./drag/useDragClick";
import { accessibleLabel } from "./OpportunityCard";

/**
 * One opportunity in the condensed board: its **client's name**, wearing its manager's colour.
 *
 * A sibling of `OpportunityCard`, not a mode inside it. The card carries five facts across three
 * rows; this carries one. A single component serving both would branch at almost every line, and
 * the two have genuinely different jobs — the card is read, this one is *counted*.
 *
 * **What is missing on purpose, and where it went.** The opportunity's own name, its value and
 * its owner's name are not here: putting them back is putting the card back. They live in the
 * shared `Tooltip` and in `aria-label`, both built from the card's own `accessibleLabel`, so a
 * screen-reader user gets the whole record and a sighted one is a hover away from it.
 *
 * **Two things colour is not allowed to carry alone.** The fill is the owner's, so ownership has
 * its non-colour carrier in the tooltip and the label. That leaves *stalled* — the fact the board
 * exists to spot — needing a mark of its own, which is why a `⚠` precedes the name rather than a
 * second hue competing with the first. A closed opportunity keeps the border treatment the card
 * uses, for the same reason.
 *
 * Chips stay draggable. A board you cannot move cards on is not the board, and the drop target is
 * still the cell, so nothing here needs pixel precision — only the grab does, and a 20px chip is
 * a smaller grab than a card. That cost was accepted knowingly
 * ([condensed-density](../../../../../specs/board/condensed-density.md)).
 */
export function OpportunityChip({
  card,
  movable,
  pending,
  onOpen,
  onBlockedDrag,
  onHover,
}: {
  card: BoardCard;
  movable: boolean;
  pending?: boolean;
  onOpen: (card: BoardCard) => void;
  onBlockedDrag?: (card: BoardCard) => void;
  onHover?: (card: BoardCard) => void;
}) {
  const t = useT();
  const draggable = useDraggable({ id: `card:${card.id}`, data: { card }, disabled: !movable || pending });

  // dnd-kit's attributes without its keyboard-drag advertising — see `OpportunityCard` for why
  // announcing an interaction that dead-ends is worse than announcing none.
  const { role, tabIndex, "aria-disabled": ariaDisabled } = draggable.attributes;

  const click = useDragClick({
    enabled: true,
    isDragging: draggable.isDragging,
    onClick: () => onOpen(card),
    onBlocked: movable ? undefined : () => onBlockedDrag?.(card),
    dragListeners: draggable.listeners,
  });

  const label = accessibleLabel(t, card, movable);

  return (
    <Tooltip content={label}>
      <span
        ref={draggable.setNodeRef}
        role={role}
        aria-disabled={ariaDisabled}
        {...click.handlers}
        onPointerEnter={() => onHover?.(card)}
        onFocus={() => onHover?.(card)}
        tabIndex={tabIndex ?? 0}
        aria-busy={pending}
        aria-label={label}
        className={cn(
          "board-chip board-type-secondary focus-visible:ring-ring inline-flex items-center gap-1 rounded border focus-visible:ring-2 focus-visible:outline-none",
          "touch-pan-y",
          movable && !pending ? "cursor-grab active:cursor-grabbing" : "cursor-pointer",
          draggable.isDragging && "opacity-40",
          pending && "opacity-60",
          // The same status borders the card uses, so a closed opportunity reads the same way in
          // both densities rather than needing a second vocabulary.
          card.status === "won" && "border-success/50",
          card.status === "lost" && "border-muted-foreground/30",
          card.status === "open" && "border-transparent",
          card.ownerColor ? "owner-tint" : "bg-muted/60",
        )}
        style={
          card.ownerColor ? ({ "--owner-color": `var(--manager-${card.ownerColor})` } as React.CSSProperties) : undefined
        }
      >
        {card.stalled && <AlertTriangle className="text-destructive size-[0.85em] shrink-0" aria-hidden />}
        {/* `truncate` against the chip's own max-width: one long client name must not own a
            whole row, which is the packing the density exists for. */}
        <span className="truncate font-medium">{card.clientName}</span>
      </span>
    </Tooltip>
  );
}
